<template>
  <b-container id="child-app"
               fluid>
    <!-- App Base -->
    <b-row class="h-100 app-row"
           no-gutters>
      <!-- Menu Section -->
      <b-col class="h-100 menu-col"
             :class="{'desktop-menu': isDesktop}"
             cols="auto">
        <sidebar></sidebar>
      </b-col>
      <!-- Content Section (Header, Router Component, Loader) -->
      <b-col class="h-100 content-col"
             cols="12">
        <!-- Content - Header Section -->
        <b-row class="header-row"
               no-gutters>
          <b-col class="header-col"
                 cols="12">
            <setup-header></setup-header>
          </b-col>
        </b-row>
        <!-- Content - Router Component Section -->
        <b-row class="h-100 router-view-row"
               id="routerViewElement"
               no-gutters
               :class="{'no-scroll': !allowScroll}"
               @click="closeMenu">
          <b-col class="h-100 router-view-col"
                 cols="12">
            <app-tooltip v-if="appCreated"></app-tooltip>
            <router-view class="router-view"
                         :class="{'mobile-scrolling': !isDesktop}"
                         role="main"
                         :aria-busy="$wait.any && !$wait.is(waitLoaderExclude)">
            </router-view>
          </b-col>
        </b-row>
        <!-- Content - Loader Overlay -->
        <activity-indicator v-if="$wait.any && !$wait.is(waitLoaderExclude)"
                            @click="closeMenu">
        </activity-indicator>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
// Elements
import ActivityIndicator from './components/common/elements/ActivityIndicator.vue'
import AppTooltip from './components/common/elements/AppTooltip.vue'
import SetupHeader from './components/common/elements/SetupHeader.vue'
import Sidebar from './components/common/elements/Sidebar.vue'

// Libraries
import AWS from 'aws-sdk'
import { mapState } from 'vuex'

// Styling
import './assets/styles/base.scss'
import './assets/styles/c1l.scss'
import './assets/styles/ccc.scss'
import './assets/styles/ccf.scss'
import './assets/styles/chs.scss'
import './assets/styles/cic.scss'
import './assets/styles/esm.scss'
import './assets/styles/inl.scss'
import './assets/styles/tmx.scss'

// Utilities
import isEmpty from './utility/isEmpty'
import logger from './utility/loggerUtil'
import router from './utility/routerUtil'
import sessionRecord from './utility/sessionRecordUtil'
import { KeyConstants } from './constants'

export default {
  name: 'child-app',

  components: {
    ActivityIndicator,
    AppTooltip,
    SetupHeader,
    Sidebar
  },

  created () {
    this.appCreated = true
  },

  mounted () {
    this.setPageBrand()

    AWS.config.update({
      region: process.env.VUE_APP_AwsRegion
    })

    // Prevents scrolling in background when modals are shown
    this.$root.$on('bv::modal::show', () => { this.$store.commit('display/preventScroll') })
    this.$root.$on('bv::modal::hide', () => { this.$store.commit('display/allowScroll') })
  },

  data () {
    return {
      waitLoaderExclude: KeyConstants.waitLoadExclude + '*',
      currentPageName: '',
      appCreated: false
    }
  },

  computed: {
    menuOpen () {
      return this.$store.state.display.sidebarOpen
    },

    isDesktop () {
      return !window.isMobileDevice
    },

    devicePixelWidth () {
      return window.screen.width
    },

    devicePixelHeight () {
      return window.screen.height
    },

    mobileBackgroundSize () {
      return this.devicePixelWidth + 'px ' + this.devicePixelHeight + 'px'
    },

    ...mapState('display', ['allowScroll'])
  },

  methods: {
    setPageBrand (to, from) {
      const bodyElement = document.querySelector('body')

      if (bodyElement.classList.length === 0) {
        bodyElement.classList.add(router.getCurrentRouteBrandClass(this.$route))
      } else {
        const toBrand = router.getCurrentRouteBrandClass(to)
        const fromBrand = router.getCurrentRouteBrandClass(from)
        if (toBrand !== fromBrand && !bodyElement.classList.contains(toBrand)) {
          bodyElement.classList.add(toBrand)
          bodyElement.classList.remove(fromBrand)
        } else {
          if (!bodyElement.classList.contains(toBrand)) {
            bodyElement.classList.add(toBrand)
          }
        }
      }
    },

    closeMenu () {
      this.$store.commit('display/closeSidebar')
    }
  },

  watch: {
    '$route' (to, from) {
      this.setPageBrand(to, from)

      this.$store.commit('display/allowScroll')

      const routeWrap = document.querySelector('.router-view-row')

      if (!isEmpty(routeWrap)) {
        routeWrap.scrollTop = document.documentElement.scrollTop = 0
      }

      this.$store.commit('display/updateHeader', { text: '' })
      this.$store.commit('session/updatePreviousPage', { previousPage: from.name, previousProps: from.params })

      try {
        if (!isEmpty(this.currentPageName)) {
          sessionRecord.logPageView()
        }
      } catch (error) {
        logger.error('Error logging screen view')
        logger.debug(error)
      }

      this.currentPageName = to.name
    }
  }
}
</script>

<style scoped>
#child-app {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  padding: 0;
}

.app-row {
  z-index: 0;
  flex-wrap: nowrap;
}

.menu-col {
  z-index: 1;
}

.menu-col.desktop-menu {
  position: fixed;
  top: 0;
  left: 0;
}

.content-col {
  z-index: 0;
}

.header-row,
.header-col {
  z-index: 3;
}

.router-view-row,
.router-view-col {
  z-index: 1;
}

.router-view-row {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.router-view-row.no-scroll {
  overflow-y: hidden !important;
  -webkit-overflow-scrolling: initial;
}

.router-view {
  flex-flow: row wrap;
  min-height: 100%;
  padding-bottom: 44px;
}

.router-view.mobile-scrolling {
  min-height: calc(100% + 1px);
}
</style>
